<script setup>
import AcceptTerms from '@/Components/AcceptTerms.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import { Head, router } from '@inertiajs/vue3';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
import { trans as $t } from 'laravel-vue-i18n';
</script>

<script>
export default {
    props: {
        errors: {
            type: Object
        },
        positions: {
            type: Array
        },
    },
    data() {
        return {
            ajaxRequest: false,
            userData: {
                position: '',
                name: '',
                email: '',
                phone: '',
                files: [],
                termsAccepted: false,
            }
        }
    },
    methods: {

        applyForJob() {

            this.ajaxRequest = true;

            router.post(route('careerApply'), this.userData, {
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                // ...
                onSuccess: page => {

                    // Success!
                    this.userData = {
                        position: '',
                        name: '',
                        email: '',
                        phone: '',
                        files: [],
                        termsAccepted: false,
                    }

                    Swal.fire({
                        icon: 'success',
                        text: $t('Köszönjük jelentkezésed, kollégánk hamarosan felveszi Veled a kapcsolatot!'),
                    });

                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    }
                },
                onFinish: visit => {
                    this.ajaxRequest = false;
                },
            });

        }

    },
    mounted() {

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'career',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
}
</script>

<style>
.btn-file {
    position: relative;
    overflow: hidden;
}

.btn-file input[type=file] {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 100%;
    min-height: 100%;
    font-size: 100px;
    text-align: right;
    filter: alpha(opacity=0);
    opacity: 0;
    outline: none;
    cursor: inherit;
    display: block;
}
</style>

<template>

    <Head :title="$t('Karrier')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('Karrier') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <div class="header-placeholder"></div>

        <div class="subpage-content">
            <div class="container subpage-container">
                <div class="row">
                    <main class="main col-12">
                        <section>
                            <div class="row section-title d-none">
                                <div class="col-12">
                                    <h2>{{ $t('Karrier') }}</h2>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <p class="fs-5 text-center" v-html="$t('Ha szeretnél egy fiatalos, lendületes csapathoz csatlakozni, ha érdekel egy nemzetközi repülőtér folyamatosan változó, izgalmas világa, jelentkezz a miniBUD által meghirdetett pozíciók egyikére!')">
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <div class="form-floating mb-3">
                                        <select class="form-select" :class="{ 'is-invalid': errors.position }" name="position" id="position" v-model="userData.position">
                                            <option value="" selected>{{ $t('Válassz pozíciót!') }}</option>
                                            <option v-for="p in positions" :value="p.title">{{ p.title }}</option>
                                        </select>
                                        <label for="position">{{ $t('Pozíció') }}</label>
                                        <div v-if="errors.position" class="invalid-feedback">{{ errors.position }}</div>
                                    </div>
                                    <template v-for="p in positions">
                                        <div v-if="p.title == userData.position" class="d-flex justify-content-between align-items-center mb-3">
                                            <i class="fas fa-info-circle fa-2x text-primary me-2"></i>
                                            <div v-html="p.description"></div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-4">
                                    <div class="form-floating mb-3">
                                        <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('Név')" v-model="userData.name" maxlength="150" autocomplete="name">
                                        <label for="name">{{ $t('Név') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                    </div>
                                </div>
                                <div class="col-12 col-md-4">
                                    <div class="form-floating mb-3">
                                        <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" v-model="userData.email" maxlength="150" autocomplete="email">
                                        <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                    </div>
                                </div>
                                <div class="col-12 col-md-4">
                                    <div class="form-floating mb-3">
                                        <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="$t('Mobiltelefonszám')" v-model="userData.phone" />
                                        <label for="phone">{{ $t('Mobiltelefonszám') }}<span class="text-danger">*</span></label>
                                        <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="row mb-3 align-items-center">
                                <div class="col-12 col-md-4">
                                    <span class="btn btn-secondary btn-file me-2">
                                        {{ $t('Önéletrajz feltöltése') }}
                                        <input type="file" id="formFile" multiple aria-labelledby="fileHelpBlock" tabindex="-1" @change="userData.files = $event.target.files">
                                    </span>
                                    <div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files']">{{ errors['files'] }}</div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files.0']">{{ errors['files.0'] }}</div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files.1']">{{ errors['files.1'] }}</div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files.2']">{{ errors['files.2'] }}</div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files.3']">{{ errors['files.3'] }}</div>
                                        <div class="text-danger fw-bold mt-2" v-if="errors['files.4']">{{ errors['files.4'] }}</div>
                                    </div>
                                </div>
                                <div class="col-12 col-md-4">
                                    <div id="fileHelpBlock" class="form-text">
                                        {{ $t('Önéletrajzod *.pdf, .*doc, *.docx, *.jpg formátumban töltheted fel. Maximum 5 db fájl feltöltése engedélyezett!') }}
                                    </div>
                                </div>
                                <div class="col-12 col-md-4">
                                    <template v-if="userData.files.length">
                                        <span class="me-2">{{ $t('Feltöltött fájlok:') }}</span><br>
                                        <span class="badge text-bg-secondary me-2" style="--bs-bg-opacity: .8" v-for="file in userData.files">{{ file.name }}</span>
                                    </template>
                                    <template v-else>
                                        <span>{{ $t('Nincs fájl feltöltve.') }}</span>
                                    </template>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <div class="form-check mb-3">
                                        <AcceptTerms class="form-check-input" :class="{ 'is-invalid': errors.termsAccepted }" id="acceptTerms" v-model="userData.termsAccepted" />
                                        <div v-if="errors.termsAccepted" class="invalid-feedback">{{ errors.termsAccepted }}</div>
                                    </div>
                                </div>
                                <div class="col-12 mb-5 text-center">
                                    <button type="submit" class="btn btn-primary px-5 text-uppercase" :disabled="ajaxRequest" @click="applyForJob">{{ $t('Küldés') }}</button>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
